import { useEffect, useMemo, useState } from 'react';
import { API_ENDPOINTS } from '../config/api.config';
import { buildMonthDays } from '../components/monthly/format';
import { FULL_DAY_MINUTES, summarizePerson } from '../components/monthly/completeness';
import { fetchHierarchy, loadPeopleMonthBundle, peekPeopleMonthBundle } from '../utils/monthlyPeopleService';

const EMPTY = {};
const ym = (year, month) => `${year}-${String(month).padStart(2, '0')}`;

/** Minuti per attività del mese (consuntivo), dai worklog dell'utente. */
async function fetchActivityMinutes(userId, year, month) {
  const last = new Date(year, month, 0).getDate();
  const params = new URLSearchParams({ user_id: String(userId), date_from: `${ym(year, month)}-01`, date_to: `${ym(year, month)}-${last}` });
  const res = await fetch(`${API_ENDPOINTS.WORKLOGS}?${params}`);
  if (!res.ok) throw new Error(`Unable to load worklogs (${res.status})`);
  const data = await res.json();
  const out = {};
  (data.worklogs || [])
    .filter((w) => String(w.user_id) === String(userId))
    .forEach((w) => {
      const name = w.activity_name || 'Other';
      out[name] = (out[name] || 0) + (Number(w.effort) || 0);
    });
  return out;
}

/**
 * Dati della Home per un mese: stato dei giorni, statistiche, ripartizione per progetto e attività.
 * Usa la stessa regola di completezza della vista PM (8h; i giorni con assenza, intera o parziale, non si controllano).
 * month 1-12.
 */
export function useHomeMonth(userId, year, month, refreshToken = 0) {
  const yearMonth = ym(year, month);
  const ids = useMemo(() => (userId && userId !== 'ALL' ? [userId] : []), [userId]);
  const [bundle, setBundle] = useState({ key: null, data: null });
  const [activities, setActivities] = useState({ key: null, data: EMPTY });
  const [error, setError] = useState(null);
  // refreshToken forza il ricaricamento dopo un salvataggio (la cache del mese va invalidata prima)
  const key = `${ids.join(',')}|${yearMonth}|${refreshToken}`;

  const data = bundle.key === key ? bundle.data : ids.length ? peekPeopleMonthBundle(ids, yearMonth) : null;

  useEffect(() => {
    if (!ids.length) return undefined;
    let cancelled = false;
    setError(null);
    loadPeopleMonthBundle(ids, yearMonth)
      .promise.then((d) => !cancelled && setBundle({ key, data: d }))
      .catch((e) => !cancelled && setError(`Unable to load your hours: ${e.message}`));
    fetchActivityMinutes(ids[0], year, month)
      .then((d) => !cancelled && setActivities({ key, data: d }))
      .catch(() => !cancelled && setActivities({ key, data: EMPTY }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const stats = useMemo(() => {
    const monthDays = buildMonthDays(year, month);
    if (!data) return { monthDays, ready: false };
    const uid = String(ids[0]);
    const person = data.filtered[uid] || { days: {}, projects: {} };
    const absences = data.absences[uid];
    const summary = summarizePerson(person.days, absences, monthDays);

    let monthTarget = 0;
    let expectedToDate = 0;
    let trackedToDate = 0;
    let workingDaysToDate = 0;
    let forecastAhead = 0;
    let fullAbsenceDays = 0;
    let partialAbsenceDays = 0;
    const days = monthDays.map((d, i) => {
      const s = summary.statuses[i];
      const absence = absences?.get(d.day);
      const target = d.weekend || absence ? 0 : FULL_DAY_MINUTES;
      const actual = person.days[d.day]?.actual || 0;
      // Monte ore del mese: giorni feriali × 8h, senza togliere le assenze
      if (!d.weekend) monthTarget += FULL_DAY_MINUTES;
      if (d.past && target) {
        expectedToDate += target;
        trackedToDate += actual;
        workingDaysToDate += 1;
      }
      if (d.future) forecastAhead += person.days[d.day]?.forecast || 0;
      if (absence && !d.weekend) {
        if (absence.full_day) fullAbsenceDays += 1;
        else partialAbsenceDays += 1;
      }
      const dateKey = `${yearMonth}-${String(d.day).padStart(2, '0')}`;
      return { ...d, ...s, actual, forecast: person.days[d.day]?.forecast || 0, absence, target, dateKey };
    });

    const projects = Object.values(person.projects)
      .map((p) => ({ name: p.name, minutes: Object.values(p.days).reduce((a, c) => a + c.actual, 0) }))
      .filter((p) => p.minutes > 0)
      .sort((a, b) => b.minutes - a.minutes);
    const activityList = Object.entries(activities.key === key ? activities.data : EMPTY)
      .map(([name, minutes]) => ({ name, minutes }))
      .filter((a) => a.minutes > 0)
      .sort((a, b) => b.minutes - a.minutes);

    return {
      ready: true,
      monthDays,
      days,
      total: summary.total,
      missing: summary.missing,
      incompleteDays: days.filter((d) => d.missing > 0),
      todayPending: days.find((d) => d.today && d.status === 'today' && !d.actual) || null,
      monthTarget,
      expectedToDate,
      trackedToDate,
      workingDaysToDate,
      avgPerWorkingDay: workingDaysToDate ? trackedToDate / workingDaysToDate : 0,
      forecastAhead,
      fullAbsenceDays,
      partialAbsenceDays,
      projects,
      activities: activityList,
    };
  }, [data, activities, key, ids, year, month, yearMonth]);

  return { ...stats, loading: !data && !error && ids.length > 0, error };
}

/**
 * Riepilogo della gerarchia del People Manager per il mese (stessa cache della vista PM:
 * aprire la vista dopo la Home è immediato).
 */
export function useTeamMonth(managerId, enabled, year, month) {
  const [state, setState] = useState({ loading: false, error: null, people: [], summaries: [] });
  const yearMonth = ym(year, month);

  useEffect(() => {
    if (!enabled || !managerId) return undefined;
    let cancelled = false;
    setState((s) => ({ ...s, loading: true, error: null }));
    (async () => {
      const people = await fetchHierarchy(managerId);
      if (!people.length) return { people, summaries: [] };
      const ids = people.map((p) => p.id);
      const { filtered, absences } = await loadPeopleMonthBundle(ids, yearMonth).promise;
      const monthDays = buildMonthDays(year, month);
      const summaries = people.map((p) => ({
        person: p,
        ...summarizePerson(filtered[String(p.id)]?.days, absences[String(p.id)], monthDays),
      }));
      return { people, summaries };
    })()
      .then((res) => !cancelled && setState({ loading: false, error: null, ...res }))
      .catch((e) => !cancelled && setState({ loading: false, error: `Unable to load your team: ${e.message}`, people: [], summaries: [] }));
    return () => {
      cancelled = true;
    };
  }, [managerId, enabled, yearMonth, year, month]);

  return state;
}
